import React, { useMemo } from "react";
import { Link as ITOLink } from "fr-ito-web-react/react/esm";
import {
  NewImage,
  ECImagePlusTextCollection,
  ECImagePlusTextVerticalExtraSmall
} from "@common/index";
import { Caption } from "@common/DynamicUikit";
import { SBlargeImages, SHlargeImages, StylingType } from "@type/index";
import { ImageComponentNames, optimizedUGCImageUrlByDevice } from "@util/image";
import { ImagePlusTextCollectionVariant } from "@common/ECImagePlusTextCollection";
import styles from "./styles.module.css";
import {
  FilterOptionType,
  FilterType
} from "../../App/TopStyling/stylingConfig";

export interface LineupCarouselItem {
  id: string;
  title: string;
  imageUrl: string;
}

interface LineupCarouselProps {
  type: StylingType;
  SBlargeList: Array<SBlargeImages>;
  SHlargeList: Array<SHlargeImages>;
  handleClick: (
    imageType: FilterType,
    item: FilterOptionType,
    gaLabel: string
  ) => void;
  currentLineup: string[];
  label: string;
}

const LineupCarousel: React.FC<LineupCarouselProps> = ({
  type,
  SBlargeList,
  SHlargeList,
  handleClick,
  currentLineup,
  label
}) => {
  const carouselNumber = useMemo(() => {
    const current = currentLineup[0] || "";
    if (!current) {
      return 0;
    }
    const index =
      type === StylingType.official
        ? SBlargeList.findIndex((item) => item.featureId === current)
        : SHlargeList.findIndex((item) => item.hashtag === current);
    return index >= 0 ? index : 0;
  }, [SBlargeList, SHlargeList, currentLineup, type]);

  // official lineup carousel item
  const renderSBItem = (item: SBlargeImages, index: number) => {
    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      // if the item is All stylings or is already selected, do nothing
      if (
        (index === 0 && currentLineup.length === 0) ||
        item.featureId === currentLineup[0]
      ) {
        return;
      }

      handleClick(
        FilterType.feature,
        {
          key: index === 0 ? "" : item.featureId || "",
          value: index === 0 ? "" : item.featureId || ""
        },
        item.featureId
      );
    };

    return (
      <div className={styles.RenderSBItem}>
        <ITOLink
          key={index}
          element="a"
          href={item.href || ""}
          target="_self"
          onClick={handleLinkClick}
        >
          <ECImagePlusTextVerticalExtraSmall
            className={styles.ImagePlusText}
            title={item.title}
            description={item.description}
            selected={carouselNumber === index}
            media={
              <NewImage
                largeImageRatio="3x4"
                largeImageUrl={optimizedUGCImageUrlByDevice(
                  item.imageUrl || "",
                  ImageComponentNames.stylingHashtagImage
                )}
                smallMediumImageRatio="3x4"
                smallMediumImageUrl={optimizedUGCImageUrlByDevice(
                  item.imageUrl || "",
                  ImageComponentNames.stylingHashtagImage
                )}
                alt={item.description}
              />
            }
          />
        </ITOLink>
      </div>
    );
  };

  // stylehint lineup carousel item
  const renderSHItem = (item: SHlargeImages, index: number) => {
    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      // if the item is All stylings or is already selected, do nothing
      if (
        (index === 0 && currentLineup.length === 0) ||
        item.hashtag === currentLineup[0]
      ) {
        return;
      }

      handleClick(
        FilterType.hashtag,
        {
          key: index === 0 ? "" : item.hashtag || "",
          value: index === 0 ? "" : item.hashtag || ""
        },
        item.outfitId ? item.outfitId.toString() : ""
      );
    };

    return (
      <div className={styles.RenderSHItem}>
        <ITOLink
          key={index}
          element="a"
          href={item.href || ""}
          target="_self"
          onClick={handleLinkClick}
        >
          <ECImagePlusTextVerticalExtraSmall
            className={styles.ImagePlusText}
            title={index !== 0 ? `#${item.hashtag}` : item.hashtag}
            selected={carouselNumber === index}
            media={
              <NewImage
                largeImageRatio="3x4"
                largeImageUrl={optimizedUGCImageUrlByDevice(
                  item.imageUrl || "",
                  ImageComponentNames.stylingHashtagImage
                )}
                smallMediumImageRatio="3x4"
                smallMediumImageUrl={optimizedUGCImageUrlByDevice(
                  item.imageUrl || "",
                  ImageComponentNames.stylingHashtagImage
                )}
                alt={item.hashtag}
              />
            }
          />
        </ITOLink>
      </div>
    );
  };

  return (
    <div className={styles.LineupCarouselContainer}>
      <ECImagePlusTextCollection
        variant={ImagePlusTextCollectionVariant.verticalExtraSmall}
      >
        {type === StylingType.official
          ? SBlargeList.map(renderSBItem)
          : SHlargeList.map(renderSHItem)}
      </ECImagePlusTextCollection>
      <Caption element="h1" color="primary-dark" className={styles.Label}>
        {label}
      </Caption>
    </div>
  );
};

export default LineupCarousel;
